import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import CodeFilesPanel from '../../components/CodeFilesPanel';

function IconArrowLeft() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12"/>
      <polyline points="12 19 5 12 12 5"/>
    </svg>
  );
}

function IconEdit() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  );
}

function IconTrash() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
      <path d="M10 11v6"/><path d="M14 11v6"/>
      <path d="M9 6V4h6v2"/>
    </svg>
  );
}

function DetailField({ label, value, mono = false }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.4px', color: 'var(--text-muted)', marginBottom: 6,
      }}>
        {label}
      </div>
      <div style={{
        color: value ? 'var(--text-primary)' : 'var(--text-muted)',
        fontFamily: mono ? 'monospace' : 'inherit',
        fontSize: mono ? 13 : 14,
        lineHeight: 1.6,
      }}>
        {value || '—'}
      </div>
    </div>
  );
}

export default function FeatureDetail() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [feature, setFeature]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    axios.get(`/features/${id}`)
      .then(res => setFeature(res.data))
      .catch(() => setError('Feature not found'))
      .finally(() => setLoading(false));
  }, [id]);

  async function handleDelete() {
    if (!window.confirm('Delete this feature and all its code files? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await axios.delete(`/features/${id}`);
      navigate('/features', { replace: true });
    } catch (e) {
      alert(e.response?.data?.error || 'Delete failed');
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <div className="loading-page">
        <div className="spinner" />
        <span>Loading feature…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-body">
        <div className="error-msg">{error}</div>
        <button className="btn btn-secondary" onClick={() => navigate('/features')}>
          <IconArrowLeft /> Back to Features
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {feature.feature_id}
        </h2>
        <p>{feature.major_feature}{feature.sub_feature ? ` › ${feature.sub_feature}` : ''}</p>
      </div>

      <div className="page-body">
        {/* Nav + actions */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 24, alignItems: 'center' }}>
          <button className="btn btn-ghost" style={{ paddingLeft: 0 }}
            onClick={() => navigate('/features')}>
            <IconArrowLeft /> All Features
          </button>
          <div style={{ flex: 1 }} />
          <button className="btn btn-secondary" onClick={() => navigate(`/features/${id}/edit`)}>
            <IconEdit /> Edit
          </button>
          <button className="btn btn-danger" disabled={deleting} onClick={handleDelete}>
            <IconTrash /> {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>

        {/* Two-column layout */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '340px 1fr',
          gap: 24,
          alignItems: 'start',
        }}>
          {/* Left column – metadata */}
          <div>
            <div className="card">
              <DetailField label="Feature ID"     value={feature.feature_id} />
              {feature.product_name && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{
                    fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                    letterSpacing: '0.4px', color: 'var(--text-muted)', marginBottom: 6,
                  }}>
                    Product
                  </div>
                  <button
                    className="btn btn-ghost"
                    style={{ padding: 0, color: 'var(--accent)', fontWeight: 600, fontSize: 14 }}
                    onClick={() => navigate(`/products/${feature.product_id}`)}
                  >
                    {feature.product_name}
                  </button>
                </div>
              )}
              <DetailField label="Major Feature"  value={feature.major_feature} />
              <DetailField label="Sub Feature"    value={feature.sub_feature} />

              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 4 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
                  CREATED
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {feature.created_at ? new Date(feature.created_at).toLocaleString() : '—'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, marginTop: 12 }}>
                  LAST UPDATED
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {feature.updated_at ? new Date(feature.updated_at).toLocaleString() : '—'}
                </div>
              </div>
            </div>
          </div>

          {/* Right column – description + code files */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Description card */}
            <div className="card">
              <div style={{
                fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.4px', color: 'var(--text-muted)', marginBottom: 10,
              }}>
                Description
              </div>
              {feature.description ? (
                <p style={{ color: 'var(--text-primary)', lineHeight: 1.7, fontSize: 14,
                  whiteSpace: 'pre-wrap' }}>
                  {feature.description}
                </p>
              ) : (
                <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  No description provided.{' '}
                  <button className="btn btn-ghost" style={{ padding: '0 4px', fontSize: 13 }}
                    onClick={() => navigate(`/features/${id}/edit`)}>
                    Add one
                  </button>
                </p>
              )}
            </div>

            {/* Code files panel */}
            <div className="card">
              <CodeFilesPanel featureId={id} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
